import { motion } from "framer-motion";
import { TrendingUp, Gift, Check, GripVertical, ShieldCheck, Heart } from "lucide-react";

const INVITE_IMG =
  "https://images.unsplash.com/photo-1737749685390-0959c05aecbb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHw0fHxtaW5pbWFsaXN0JTIwd2VkZGluZyUyMGludml0YXRpb258ZW58MHx8fHwxNzkyMjIzODk4fDA&ixlib=rb-4.1.0&q=85";

// ---------- Hero: lifestyle image (couple using the iuni dashboard) ----------
export function HeroVisual() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
      className="relative"
      data-testid="hero-visual"
    >
      {/* sunset ambient glow */}
      <div
        className="absolute -inset-8 rounded-[3rem] opacity-25 blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle at 60% 40%, #E07A5F, transparent 65%)" }}
      />
      <div className="relative rounded-[2rem] overflow-hidden shadow-[0_30px_80px_-20px_rgba(0,0,0,0.25)] border border-white">
        <img
          src="/hero-couple.webp"
          alt="Coppia che organizza il proprio matrimonio con la dashboard di iuni su tablet"
          className="w-full aspect-[4/3] object-cover object-center"
          data-testid="hero-image"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#2D2926]/15 via-transparent to-transparent pointer-events-none" />
      </div>

      {/* floating cards */}
      <motion.div
        animate={{ y: [0, -8, 0] }}
        transition={{ repeat: Infinity, duration: 5, ease: "easeInOut" }}
        className="hidden sm:flex absolute -left-8 top-14 items-center gap-3 bg-white/90 backdrop-blur-xl rounded-2xl shadow-[0_16px_40px_-12px_rgba(0,0,0,0.15)] border border-white px-4 py-3"
      >
        <span className="w-8 h-8 rounded-xl bg-[rgba(224,122,95,0.12)] flex items-center justify-center">
          <TrendingUp size={15} className="text-[#E07A5F]" />
        </span>
        <div>
          <p className="text-xs font-semibold text-[#2D2926]">+3 RSVP</p>
          <p className="text-[10px] text-gray-400">questa settimana</p>
        </div>
      </motion.div>
      <motion.div
        animate={{ y: [0, 9, 0] }}
        transition={{ repeat: Infinity, duration: 6, ease: "easeInOut", delay: 1 }}
        className="hidden sm:flex absolute -right-6 bottom-12 items-center gap-3 bg-white/90 backdrop-blur-xl rounded-2xl shadow-[0_16px_40px_-12px_rgba(0,0,0,0.15)] border border-white px-4 py-3"
      >
        <span className="w-8 h-8 rounded-xl bg-[rgba(224,122,95,0.12)] flex items-center justify-center">
          <Gift size={15} className="text-[#E07A5F]" />
        </span>
        <div>
          <p className="text-xs font-semibold text-[#2D2926]">Busta Digitale</p>
          <p className="text-[10px] text-gray-400">€250 ricevuti</p>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ---------- Feature 1: elegant mobile invitation ----------
export function InviteMockup() {
  return (
    <motion.div
      animate={{ y: [0, -10, 0] }}
      transition={{ repeat: Infinity, duration: 7, ease: "easeInOut" }}
      className="relative mx-auto w-64 sm:w-72"
      data-testid="invite-mockup"
    >
      <div className="rounded-[2.2rem] border border-gray-100 bg-white shadow-[0_30px_80px_-20px_rgba(0,0,0,0.18)] overflow-hidden">
        <img src={INVITE_IMG} alt="Invito digitale elegante" className="h-36 w-full object-cover" />
        <div className="p-6 text-center">
          <p className="text-[10px] font-semibold tracking-[0.28em] text-gray-400">SI SPOSANO</p>
          <p className="font-display text-2xl text-[#2D2926] mt-1.5">Marco &amp; Giulia</p>
          <p className="text-xs text-gray-500 mt-1">12 Settembre 2026 · Villa del Lago</p>
          <div className="mt-5 w-full rounded-xl py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-[#E07A5F] to-[#E2856E]">
            Sì, ci sarò
          </div>
          <div className="mt-2 w-full rounded-xl py-2.5 text-sm text-gray-500 border border-gray-200">
            Non posso venire
          </div>
          <p className="mt-4 text-[11px] text-gray-400">
            Intolleranze o pernotto? Rispondi in un tap.
          </p>
        </div>
      </div>
    </motion.div>
  );
}

// ---------- Feature 2: drag-and-drop seating UI ----------
export function SeatingMockup() {
  const guests = ["Zia Anna", "Marco R.", "Giulia B.", "Nonno Piero"];
  return (
    <div
      className="bg-white rounded-3xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.14)] border border-gray-100 p-6 sm:p-8 max-w-md mx-auto w-full"
      data-testid="seating-mockup"
    >
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm font-semibold text-[#2D2926]">Disposizione tavoli</p>
        <span className="text-[10px] font-semibold tracking-widest text-[#C2492F] bg-[rgba(224,122,95,0.1)] px-2.5 py-1 rounded-full">
          AUTO
        </span>
      </div>
      <div className="grid grid-cols-[1fr_auto] gap-6 items-center">
        <div className="space-y-2.5">
          {guests.map((g, i) => (
            <motion.div
              key={g}
              animate={i === 1 ? { rotate: [-1.5, 1.5, -1.5], scale: [1, 1.04, 1] } : {}}
              transition={i === 1 ? { repeat: Infinity, duration: 3 } : {}}
              className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-xs font-medium ${
                i === 1
                  ? "border-[#E07A5F]/40 bg-[rgba(224,122,95,0.06)] text-[#C2492F] shadow-md"
                  : "border-gray-100 bg-[#FBFBFB] text-gray-600"
              }`}
            >
              <GripVertical size={13} className="text-gray-300" />
              {g}
            </motion.div>
          ))}
        </div>
        <div className="relative w-32 h-32">
          <div className="absolute inset-0 rounded-full border-2 border-dashed border-[#E07A5F]/40" />
          <div className="absolute inset-6 rounded-full bg-white border border-gray-100 shadow-inner flex items-center justify-center">
            <p className="text-[9px] tracking-[0.18em] text-gray-400">TAVOLO 4</p>
          </div>
          {Array.from({ length: 6 }).map((_, i) => (
            <span
              key={i}
              className={`absolute w-3 h-3 rounded-full ${
                i === 2
                  ? "border-2 border-dashed border-[#E07A5F]/60 bg-white"
                  : "bg-gradient-to-br from-[#E07A5F] to-[#E2856E]"
              }`}
              style={{
                top: "50%",
                left: "50%",
                transform: `translate(-50%, -50%) rotate(${i * 60}deg) translateY(-3.7rem)`,
              }}
            />
          ))}
        </div>
      </div>
      <div className="mt-6 flex items-center gap-2 text-xs text-gray-500 border-t border-gray-100 pt-4">
        <Check size={14} className="text-[#E07A5F]" />
        Planimetria catering generata · 12 tavoli
      </div>
    </div>
  );
}

// ---------- Feature 3: secure checkout ----------
export function CheckoutMockup() {
  return (
    <div
      className="bg-white rounded-3xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.14)] border border-gray-100 p-6 sm:p-8 max-w-sm mx-auto w-full"
      data-testid="checkout-mockup"
    >
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-2xl bg-[rgba(224,122,95,0.12)] flex items-center justify-center">
          <Heart size={17} className="text-[#E07A5F]" fill="#E07A5F" />
        </span>
        <div>
          <p className="text-sm font-semibold text-[#2D2926]">Busta Digitale</p>
          <p className="text-[11px] text-gray-400">da Zia Anna e Zio Carlo</p>
        </div>
      </div>
      <p className="font-display text-4xl text-[#2D2926] mt-5">€250,00</p>
      <div className="mt-5 space-y-2.5">
        <div className="w-full rounded-xl bg-[#1A1A1A] text-white text-sm font-medium py-3 text-center">
          Paga con Apple Pay
        </div>
        <div className="w-full rounded-xl border border-gray-200 text-sm font-medium py-3 text-center text-gray-600">
          Google Pay
        </div>
      </div>
      <div className="mt-6 flex items-start gap-2.5 border-t border-gray-100 pt-5">
        <ShieldCheck size={16} className="text-[#E07A5F] mt-0.5 shrink-0" />
        <div>
          <p className="text-xs font-semibold text-[#2D2926]">Protezione Escrow attiva</p>
          <p className="text-[11px] text-gray-400 mt-0.5">
            I fondi ai fornitori vengono rilasciati solo a servizio completato.
          </p>
        </div>
      </div>
      <p className="mt-3 text-[10px] text-gray-400 text-center">
        Fee trasparente del 3% · nessun costo nascosto
      </p>
    </div>
  );
}
